import React, { useRef, useMemo, useEffect, useState } from 'react';
import { Stack, Typography, SxProps } from '@mui/material';
import { Form, FormRef } from 'common/form';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useUser } from 'core/stores/hooks/useUser';
import { ToastType, useToast } from 'services/toast';
import { useUpdateOrganizationConfigMutation } from 'generated-graphql/hooks/apollo-hooks';
import { useSetRecoilState } from 'recoil';
import { organizationConfigAtom } from 'services/organization/atom';
import {
  conversationSettingsSchema,
  ConversationSettingsFormData
} from '../forms/conversationSettingsSchema';
import { getDefaultRejectionMessage } from 'modules/campaign-creation/utils/getDefaultRejectionMessage';
import { getDefaultClosingMessage } from 'modules/campaign-creation/utils/getDefaultClosingMessage';
import { getDefaultCustomRequest } from 'modules/campaign-creation/utils/getDefaultCustomRequest';
import { getDefaultAgentName } from 'modules/campaign-creation/utils/getDefaultAgentName';
import { Tone } from 'generated-graphql/types';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { getConversationSettingsFields } from 'modules/organization/forms/getConversationSettingsFields';
import { getConversationSettingsGroups } from 'modules/organization/forms/getConversationSettingsGroups';
import { handleAgentUpsert } from 'modules/campaign-creation/utils/handleAgentUpsert';
import { AlertCard, FailedMessageWithHref } from 'common/ui';
import { useModalV2 } from 'common/modal/hooks/useModalV2';

const containerStack: SxProps = {
  gap: '40px',
  py: '20px'
};

const titleTypography: SxProps = {
  fontSize: '20px',
  mt: '20px'
};

export const ConversationSettingsDrawer: React.FC = () => {
  const formRef = useRef<FormRef<ConversationSettingsFormData>>(null);
  const organizationConfig = useOrganizationConfig();
  const organization = useOrganization();
  const user = useUser();
  const { showToast } = useToast();
  const [updateOrganizationConfig] = useUpdateOrganizationConfigMutation();
  const [isUpdating, setIsUpdating] = useState(false);
  const setOrganizationConfig = useSetRecoilState(organizationConfigAtom);
  const { closeDrawer } = useDrawerV2();
  const { openModal } = useModalV2();

  const [currentFormData, setCurrentFormData] =
    useState<ConversationSettingsFormData | null>(null);

  const defaultValues = useMemo(() => {
    return {
      successfulMessage: getDefaultCustomRequest(organizationConfig),
      unsuccessfulMessage: getDefaultRejectionMessage({
        isEngagementOutreach: false,
        organizationConfig,
        savedRejectionMessage: organizationConfig?.rejectionMessage,
        userEmail: user.email,
        websiteUrl: organization.websiteUrl || undefined
      }),
      notInterestedMessage: getDefaultClosingMessage({
        organizationConfig,
        savedClosingMessage: organizationConfig?.closingMessage,
        userEmail: user.email
      }),
      agentName: getDefaultAgentName({
        organizationConfig,
        agentName: organizationConfig?.agent?.name || undefined,
        agentId: organizationConfig?.agentId || undefined,
        userName: user.name || ''
      }),
      agentTone: organizationConfig?.agent?.tone || Tone.Neutral
    };
  }, [organizationConfig, user, organization]);

  // Watch form changes to track current state
  useEffect(() => {
    if (!formRef.current) return;

    const subscription = formRef.current.watch(
      (value: ConversationSettingsFormData) => {
        setCurrentFormData(value);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Check if form has been modified from defaults
  const hasChanges = useMemo(() => {
    if (!currentFormData) return false;

    return (
      currentFormData.successfulMessage !== defaultValues.successfulMessage ||
      currentFormData.unsuccessfulMessage !==
        defaultValues.unsuccessfulMessage ||
      currentFormData.notInterestedMessage !==
        defaultValues.notInterestedMessage ||
      currentFormData.agentName !== defaultValues.agentName ||
      currentFormData.agentTone !== defaultValues.agentTone
    );
  }, [currentFormData, defaultValues]);

  const revertChanges = () => {
    if (!formRef.current) return;

    formRef.current.setValue(
      'successfulMessage',
      defaultValues.successfulMessage
    );
    formRef.current.setValue(
      'unsuccessfulMessage',
      defaultValues.unsuccessfulMessage
    );
    formRef.current.setValue(
      'notInterestedMessage',
      defaultValues.notInterestedMessage
    );
    formRef.current.setValue('agentName', defaultValues.agentName);
    formRef.current.setValue('agentTone', defaultValues.agentTone);
  };

  const handleRevert = () => {
    openModal({
      title: 'Any changes will be lost',
      subtitle: 'Do you want to continue?',
      primaryButtonText: 'Cancel',
      secondaryButtonText: 'Yes',
      primaryButtonProps: { variant: 'primary' },
      secondaryButtonProps: { variant: 'tertiary', state: 'destructive' },
      onSecondaryAction: revertChanges // Delete action
    });
  };

  const handleSubmit = async (data: ConversationSettingsFormData) => {
    setIsUpdating(true);
    let agentId = organizationConfig?.agentId;

    try {
      if (!organizationConfig?.id) {
        showToast({
          title: 'Something went wrong',
          message: (
            <FailedMessageWithHref emailSubject="Failed to update conversation settings" />
          ),
          type: ToastType.Error
        });
        return;
      }

      if (
        data.agentName !== organizationConfig?.agent?.name ||
        data.agentTone !== (organizationConfig?.agent?.tone as Tone)
      ) {
        agentId = await handleAgentUpsert({
          agentName: data.agentName,
          agentTone: data.agentTone,
          organizationId: organization.id,
          userId: user.id
        });
      }

      const result = await updateOrganizationConfig({
        variables: {
          input: {
            id: organizationConfig.id,
            customRequest: data.successfulMessage,
            rejectionMessage: data.unsuccessfulMessage,
            closingMessage: data.notInterestedMessage,
            agentId
          }
        }
      });

      const updatedOrganizationConfig = result.data?.updateOrganizationConfig;

      if (updatedOrganizationConfig) {
        setOrganizationConfig((prevConfig) => ({
          ...prevConfig,
          ...updatedOrganizationConfig
        }));
      }

      showToast({
        type: ToastType.Success,
        title: 'Conversation settings updated'
      });

      closeDrawer();
    } catch (error) {
      showToast({
        title: 'Something went wrong',
        message: (
          <FailedMessageWithHref emailSubject="Failed to update conversation settings" />
        ),
        type: ToastType.Error
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Stack direction="column" sx={containerStack}>
      <Typography sx={titleTypography}>Closing messages</Typography>

      <Form
        ref={formRef}
        schema={conversationSettingsSchema}
        fields={getConversationSettingsFields()}
        groups={getConversationSettingsGroups()}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        submitButtonProps={{
          label: 'Update',
          loading: isUpdating
        }}
        onSecondaryButtonClick={hasChanges ? handleRevert : undefined}
        secondaryButtonProps={
          hasChanges
            ? {
                label: 'Revert',
                variant: 'link'
              }
            : undefined
        }
      >
        <AlertCard type="info" title="Changes only apply to new campaigns.">
          <Typography>
            Draft, ready to launch, scheduled and live campaigns will not be
            affected.
          </Typography>
        </AlertCard>
      </Form>
    </Stack>
  );
};
